<template>
  <TransitionRoot
    as="template"
    :show="open"
  >
    <Dialog
      as="div"
      class="fixed inset-0 z-[1680] overflow-hidden"
      @close="handleClose"
    >
      <TransitionChild
        as="template"
        enter="ease-out duration-250"
        enter-from="opacity-0"
        enter-to="opacity-100"
        leave="ease-in duration-200"
        leave-from="opacity-100"
        leave-to="opacity-0"
      >
        <div class="fixed inset-0 bg-black/60 backdrop-blur-[1px]" />
      </TransitionChild>

      <div class="app-scroll fixed inset-0 overflow-y-auto px-4 py-6 sm:px-6 lg:px-8">
        <div class="flex min-h-full items-start justify-center">
          <TransitionChild
            as="template"
            enter="ease-out duration-250"
            enter-from="opacity-0 translate-y-2 sm:translate-y-4 sm:scale-95"
            enter-to="opacity-100 translate-y-0 sm:scale-100"
            leave="ease-in duration-200"
            leave-from="opacity-100 translate-y-0 sm:scale-100"
            leave-to="opacity-0 translate-y-2 sm:translate-y-4 sm:scale-95"
          >
            <DialogPanel class="w-full max-w-[110rem] overflow-hidden rounded-2xl bg-white shadow-2xl">
              <div class="border-b border-zinc-200 px-4 py-3 sm:px-5">
                <div class="flex items-start justify-between gap-3">
                  <div>
                    <DialogTitle class="text-xl font-semibold text-zinc-900">
                      Bulk Upload {{ targetTypeLabel }}
                    </DialogTitle>
                    <p class="mt-1 text-sm text-zinc-500">
                      Upload, map, preview, then queue import safely.
                    </p>
                  </div>
                  <div class="flex items-center gap-2">
                    <button
                      type="button"
                      class="rounded-lg border border-zinc-300 bg-white px-4 py-2 text-sm font-semibold text-zinc-700 hover:bg-zinc-50"
                      @click="handleBackOrClose"
                    >
                      {{ step === 'source' ? 'Cancel' : (step === 'mapping' ? 'Back' : 'Close') }}
                    </button>
                    <button
                      v-if="step === 'source'"
                      type="button"
                      class="rounded-lg bg-[#2D8CFF] px-4 py-2 text-sm font-semibold text-white hover:bg-[#2d8bffce] disabled:cursor-not-allowed disabled:opacity-70"
                      :disabled="previewLoading || !canContinueToPreview"
                      @click="runPreview"
                    >
                      {{ previewLoading ? 'Loading...' : 'Next' }}
                    </button>
                    <button
                      v-if="step === 'mapping'"
                      type="button"
                      class="rounded-lg bg-[#2D8CFF] px-4 py-2 text-sm font-semibold text-white hover:bg-[#2d8bffce] disabled:cursor-not-allowed disabled:opacity-70"
                      :disabled="startLoading || previewBlockingIssues.length > 0 || previewSummary.blockingRows > 0"
                      @click="startImport"
                    >
                      {{ startLoading ? 'Queueing...' : 'Start Import' }}
                    </button>
                    <button
                      type="button"
                      class="rounded-full p-2 text-zinc-500 transition hover:bg-zinc-100 hover:text-zinc-700"
                      @click="handleClose"
                    >
                      <XMarkIcon class="h-6 w-6" />
                    </button>
                  </div>
                </div>
              </div>

              <div
                class="app-scroll max-h-[86vh] overflow-x-hidden px-4 py-3 pb-8 sm:px-5"
                :class="step === 'source' ? 'overflow-y-hidden' : 'overflow-y-auto'"
              >
                <div
                  v-if="errorMessage"
                  class="mb-4 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-600"
                >
                  {{ errorMessage }}
                </div>

                <div v-if="step === 'source'">
                  <div class="mb-4 flex items-center justify-between gap-3 border-b border-zinc-200">
                    <div class="flex items-center gap-3">
                      <button
                        type="button"
                        class="border-b-2 px-1 pb-2 text-sm font-semibold"
                        :class="sourceTab === 'paste' ? 'border-[#2D8CFF] text-[#2D8CFF]' : 'border-transparent text-zinc-500'"
                        @click="sourceTab = 'paste'"
                      >
                        Platforma Spreadsheet Ingestion
                      </button>
                      <button
                        type="button"
                        class="border-b-2 px-1 pb-2 text-sm font-semibold"
                        :class="sourceTab === 'upload' ? 'border-[#2D8CFF] text-[#2D8CFF]' : 'border-transparent text-zinc-500'"
                        @click="sourceTab = 'upload'"
                      >
                        Upload file
                      </button>
                    </div>
                    <div class="mb-2 flex items-center gap-2">
                      <button
                        v-if="sourceTab === 'paste'"
                        type="button"
                        class="rounded-lg border border-zinc-300 bg-white px-3 py-1.5 text-xs font-semibold text-zinc-700 transition hover:bg-zinc-50"
                        @click="appendSpreadsheetRows(250)"
                      >
                        + 250 Rows
                      </button>
                      <button
                        v-if="sourceTab === 'paste'"
                        type="button"
                        class="rounded-lg border border-zinc-300 bg-white px-3 py-1.5 text-xs font-semibold text-zinc-700 transition hover:bg-zinc-50"
                        @click="clearSpreadsheetRows"
                      >
                        Clear
                      </button>
                      <button
                        type="button"
                        class="rounded-lg border border-zinc-300 bg-white px-3 py-1.5 text-xs font-semibold text-zinc-700 transition hover:bg-zinc-50"
                        @click="downloadTemplateCsv"
                      >
                        Download Template CSV
                      </button>
                    </div>
                  </div>

                  <div
                    v-if="sourceTab === 'upload'"
                    class="rounded-xl border border-zinc-200 bg-zinc-50 p-4"
                  >
                    <div
                      class="rounded-xl border-2 border-dashed px-6 py-12 text-center"
                      :class="isDragOver ? 'border-[#2D8CFF] bg-blue-50' : 'border-zinc-300 bg-white'"
                      @dragover.prevent="isDragOver = true"
                      @dragleave.prevent="isDragOver = false"
                      @drop.prevent="handleDropCsv"
                    >
                      <DocumentArrowUpIcon class="mx-auto h-10 w-10 text-zinc-400" />
                      <p class="mt-3 text-base font-semibold text-zinc-700">
                        Drag a file here
                      </p>
                      <p class="text-sm text-zinc-500">
                        or
                        <button
                          type="button"
                          class="font-semibold text-[#2D8CFF] hover:text-[#2d8bffce]"
                          @click="openFilePicker"
                        >
                          select a file from your computer
                        </button>
                      </p>
                      <p class="mt-2 text-xs text-zinc-400">
                        CSV up to 50MB
                      </p>
                      <input
                        ref="fileInputRef"
                        type="file"
                        accept=".csv,text/csv"
                        class="hidden"
                        @change="handleCsvFileChosen"
                      >
                    </div>
                    <p
                      v-if="uploadedFileName"
                      class="mt-3 text-sm text-zinc-600"
                    >
                      Loaded: <span class="font-semibold">{{ uploadedFileName }}</span>
                    </p>
                  </div>

                  <div
                    v-else
                    class="rounded-xl border border-zinc-200 bg-zinc-50 p-4"
                  >
                    <div class="mb-2 rounded-xl border border-zinc-200 bg-white p-2.5">
                      <div class="flex flex-wrap items-end gap-3">
                        <div class="min-w-[220px]">
                          <label class="block text-[11px] font-semibold uppercase tracking-wide text-zinc-500">Selected cell</label>
                          <p class="mt-1 text-sm font-semibold text-zinc-800">
                            {{ selectedCellLabel }}
                          </p>
                          <p class="text-[11px] text-zinc-500">
                            {{ selectedCellFieldLabel }}
                          </p>
                        </div>
                        <div class="min-w-[250px]">
                          <label class="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-zinc-500">Subscription options</label>
                          <div class="flex items-center gap-2">
                            <PlatformaSelect
                              v-model="helperSelections.planTierSlug"
                              class="w-full"
                              :options="planTierSlugOptions"
                              placeholder="Select subscription"
                              menu-placement="bottom"
                            />
                            <button
                              type="button"
                              class="rounded-md border border-zinc-300 bg-white px-2.5 py-1.5 text-[11px] font-semibold text-zinc-700 hover:bg-zinc-50"
                              @click="insertIntoSelectedCell(helperSelections.planTierSlug)"
                            >
                              Insert
                            </button>
                          </div>
                        </div>
                        <div class="min-w-[220px]">
                          <label class="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-zinc-500">Status options</label>
                          <div class="flex items-center gap-2">
                            <PlatformaSelect
                              v-model="helperSelections.status"
                              class="w-full"
                              :options="statusCellOptions"
                              placeholder="Select status"
                              menu-placement="bottom"
                            />
                            <button
                              type="button"
                              class="rounded-md border border-zinc-300 bg-white px-2.5 py-1.5 text-[11px] font-semibold text-zinc-700 hover:bg-zinc-50"
                              @click="insertIntoSelectedCell(helperSelections.status)"
                            >
                              Insert
                            </button>
                          </div>
                        </div>
                        <div class="min-w-[220px]">
                          <label class="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-zinc-500">Send invite email</label>
                          <div class="flex items-center gap-2">
                            <PlatformaSelect
                              v-model="helperSelections.sendInviteEmail"
                              class="w-full"
                              :options="sendInviteCellOptions"
                              placeholder="true/false"
                              menu-placement="bottom"
                            />
                            <button
                              type="button"
                              class="rounded-md border border-zinc-300 bg-white px-2.5 py-1.5 text-[11px] font-semibold text-zinc-700 hover:bg-zinc-50"
                              @click="insertIntoSelectedCell(helperSelections.sendInviteEmail)"
                            >
                              Insert
                            </button>
                          </div>
                        </div>
                        <div class="min-w-[240px]">
                          <label class="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-zinc-500">Import mode</label>
                          <PlatformaSelect
                            v-model="importMode"
                            class="w-full"
                            :options="importModeOptions"
                            menu-placement="bottom"
                          />
                        </div>
                        <div class="min-w-[320px]">
                          <label class="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-zinc-500">Permission</label>
                          <label class="flex h-[38px] items-center gap-2 rounded-md border border-zinc-200 bg-zinc-50 px-3 text-[12px] text-zinc-700">
                            <input
                              v-model="permissionConfirmed"
                              type="checkbox"
                              class="h-4 w-4 rounded border-zinc-300 text-[#2d8bff] focus:ring-0"
                            >
                            I have permission to add/update these contacts.
                          </label>
                        </div>
                      </div>
                    </div>

                    <div class="border border-zinc-200 bg-white overflow-hidden flex h-[54vh] min-h-[340px] lg:h-[58vh] flex-col">
                      <div
                        ref="sheetViewportRef"
                        class="sheet-grid-scroll app-scroll flex-1 overflow-y-scroll overflow-x-hidden"
                        @scroll="handleSheetViewportScroll"
                      >
                        <table
                          class="table-fixed border-collapse text-[12px]"
                          :style="{ width: `${sheetContentWidthPx}px` }"
                        >
                          <colgroup>
                            <col :style="{ width: `${SHEET_ROW_HEADER_WIDTH}px`, minWidth: `${SHEET_ROW_HEADER_WIDTH}px` }">
                            <col
                              v-for="(_header, colIndex) in spreadsheetHeaders"
                              :key="`sheet-colgroup-${colIndex}`"
                              :style="getColumnStyle(colIndex)"
                            >
                          </colgroup>
                          <thead>
                            <tr class="bg-zinc-100">
                              <th class="sheet-th-letter sheet-th-left-col sheet-th-corner-top w-[52px] border border-zinc-200 px-2 py-0 text-center text-[11px] font-semibold text-zinc-500">
                                #
                              </th>
                              <th
                                v-for="(header, colIndex) in spreadsheetHeaders"
                                :key="`sheet-col-letter-${header}`"
                                class="sheet-th-letter sheet-col-letter-header border border-zinc-200 px-2 py-0 text-center text-[11px] font-semibold text-zinc-500"
                              >
                                <div class="relative flex items-center justify-center">
                                  <span>{{ spreadsheetColumnLetters[colIndex] }}</span>
                                  <span
                                    class="sheet-col-resize-handle"
                                    title="Drag to resize column"
                                    @mousedown.prevent="startColumnResize(colIndex, $event)"
                                  />
                                </div>
                              </th>
                            </tr>
                            <tr class="bg-zinc-50">
                              <th class="sheet-th-field sheet-th-left-col sheet-th-corner-bottom border border-zinc-200 px-2 py-1.5 text-center text-[11px] font-semibold uppercase tracking-wide text-zinc-600">
                                Field
                              </th>
                              <th
                                v-for="header in spreadsheetHeaders"
                                :key="`sheet-col-header-${header}`"
                                class="sheet-th-field border border-zinc-200 px-2 py-2 text-left text-[11px] font-semibold uppercase tracking-wide text-zinc-700"
                              >
                                <div class="flex items-center gap-1">
                                  <span class="truncate">{{ header }}</span>
                                  <div class="relative inline-flex items-center group">
                                    <button
                                      type="button"
                                      class="inline-flex h-4 w-4 items-center justify-center rounded-full border border-zinc-300 text-[10px] font-bold text-zinc-500 transition hover:bg-zinc-100"
                                    >
                                      ?
                                    </button>
                                    <div class="pointer-events-none absolute left-1/2 top-6 z-40 w-64 -translate-x-1/2 rounded-lg bg-black/80 px-2 py-1.5 text-[10px] leading-4 text-white opacity-0 transition group-hover:opacity-100">
                                      {{ spreadsheetColumnHints[header] || 'No additional formatting hint.' }}
                                    </div>
                                  </div>
                                </div>
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr
                              v-for="(row, rowIndex) in spreadsheetRows"
                              :key="`sheet-row-${rowIndex}`"
                              :style="getRowStyle(rowIndex)"
                            >
                              <th class="sheet-row-header sheet-left-col border border-zinc-200 bg-zinc-50 p-0 text-center text-[11px] font-semibold text-zinc-500 align-middle">
                                <div
                                  class="relative flex h-full w-full items-center justify-center"
                                  :style="{ minHeight: `${getRowHeight(rowIndex)}px`, height: `${getRowHeight(rowIndex)}px` }"
                                >
                                  <span>{{ rowIndex + 2 }}</span>
                                  <span
                                    class="sheet-row-resize-handle"
                                    title="Drag to resize row"
                                    @mousedown.prevent="startRowResize(rowIndex, $event)"
                                  />
                                </div>
                              </th>
                              <td
                                v-for="(header, colIndex) in spreadsheetHeaders"
                                :key="`sheet-cell-${rowIndex}-${header}`"
                                class="sheet-cell border border-zinc-200 p-0 align-top"
                                :class="isCellSelected(rowIndex, colIndex) ? 'sheet-cell-selected bg-blue-50/40' : ''"
                                @click="onCellClick(rowIndex, colIndex, $event)"
                              >
                                <textarea
                                  v-model="row[header]"
                                  :data-sheet-row="rowIndex"
                                  :data-sheet-col="colIndex"
                                  rows="1"
                                  class="app-scroll sheet-cell-input block h-full w-full overflow-hidden border-0 bg-white px-2 text-[12px] font-medium text-zinc-900 placeholder:text-zinc-300 focus:outline-none focus:ring-0"
                                  placeholder=""
                                  :style="getCellInputStyle(rowIndex)"
                                  @focus="setSelectedCell(rowIndex, colIndex)"
                                  @paste="handleCellPaste($event, rowIndex, colIndex)"
                                />
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                      <div
                        ref="sheetBottomScrollerRef"
                        class="sheet-bottom-scroll sheet-x-scroll app-scroll flex-none overflow-x-scroll overflow-y-hidden"
                        @scroll="handleSheetBottomScroll"
                      >
                        <div :style="{ width: `${sheetContentWidthPx}px`, height: '1px' }" />
                      </div>
                    </div>
                  </div>
                </div>

                <div v-else-if="step === 'mapping'">
                  <div class="rounded-xl border border-zinc-200 bg-zinc-50 p-4">
                    <div class="mb-3 flex flex-wrap items-center justify-between gap-2">
                      <h4 class="text-sm font-semibold uppercase tracking-wide text-zinc-600">
                        Column mapping
                      </h4>
                      <button
                        type="button"
                        class="rounded-lg border border-zinc-300 bg-white px-3 py-1.5 text-xs font-semibold text-zinc-700 hover:bg-zinc-50"
                        :disabled="previewLoading"
                        @click="runPreview"
                      >
                        {{ previewLoading ? 'Refreshing...' : 'Refresh Preview' }}
                      </button>
                    </div>
                    <div class="grid grid-cols-1 gap-3 md:grid-cols-2">
                      <div
                        v-for="header in headers"
                        :key="`map-${header}`"
                        class="rounded-md border border-zinc-200 bg-white p-2"
                      >
                        <p class="truncate text-xs font-semibold uppercase tracking-wide text-zinc-500">
                          {{ header }}
                        </p>
                        <PlatformaSelect
                          v-model="mapping[header]"
                          class="mt-2"
                          :options="mappingOptions"
                          menu-placement="top"
                        />
                      </div>
                    </div>
                  </div>

                  <div class="mt-4 rounded-xl border border-zinc-200 bg-white p-4">
                    <p class="text-sm text-zinc-700">
                      Rows: <span class="font-semibold">{{ previewSummary.totalRows }}</span>
                      | Previewed: <span class="font-semibold">{{ previewRows.length }}</span>
                      | Warning rows: <span class="font-semibold">{{ previewSummary.warningRows }}</span>
                      | Blocking rows: <span class="font-semibold">{{ previewSummary.blockingRows }}</span>
                    </p>
                  </div>
                  <div
                    v-if="previewBlockingIssues.length"
                    class="mt-3 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-600"
                  >
                    <p
                      v-for="issue in previewBlockingIssues"
                      :key="issue"
                    >
                      {{ issue }}
                    </p>
                  </div>

                  <div class="app-scroll mt-4 max-h-[30vh] overflow-auto rounded-xl border border-zinc-200">
                    <table class="min-w-full divide-y divide-zinc-200 text-sm">
                      <thead class="bg-zinc-50">
                        <tr>
                          <th class="px-3 py-2 text-left font-semibold text-zinc-700">
                            Row
                          </th>
                          <th class="px-3 py-2 text-left font-semibold text-zinc-700">
                            Email
                          </th>
                          <th class="px-3 py-2 text-left font-semibold text-zinc-700">
                            Name
                          </th>
                          <th class="px-3 py-2 text-left font-semibold text-zinc-700">
                            Warnings
                          </th>
                        </tr>
                      </thead>
                      <tbody class="divide-y divide-zinc-100 bg-white">
                        <tr
                          v-for="row in previewRows"
                          :key="`preview-row-${row.rowNumber}`"
                        >
                          <td class="px-3 py-2 text-zinc-700">
                            {{ row.rowNumber }}
                          </td>
                          <td class="px-3 py-2 text-zinc-700">
                            {{ row.normalized?.email || 'â€”' }}
                          </td>
                          <td class="px-3 py-2 text-zinc-700">
                            {{ [row.normalized?.first_name, row.normalized?.last_name].filter(Boolean).join(' ') || 'â€”' }}
                          </td>
                          <td class="px-3 py-2 text-zinc-600">
                            {{ [...(row.warnings || []), ...(row.blockingErrors || [])].join(' | ') || 'None' }}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>

                <div
                  v-else
                  class="rounded-xl border border-zinc-200 bg-zinc-50 p-4"
                >
                  <h4 class="text-sm font-semibold uppercase tracking-wide text-zinc-600">
                    Import Progress
                  </h4>
                  <p class="mt-2 text-sm text-zinc-700">
                    Status: <span class="font-semibold">{{ jobStatus.status || 'queued' }}</span>
                    | Processed: <span class="font-semibold">{{ jobStatus.processedRows }}</span>/<span class="font-semibold">{{ jobStatus.totalRows }}</span>
                    | Success: <span class="font-semibold">{{ jobStatus.successRows }}</span>
                    | Failed: <span class="font-semibold">{{ jobStatus.failedRows }}</span>
                    | Skipped: <span class="font-semibold">{{ jobStatus.skippedRows }}</span>
                  </p>
                  <div
                    v-if="runningHumanMessage"
                    :class="runningHumanMessageClass"
                  >
                    {{ runningHumanMessage }}
                  </div>
                  <div
                    v-if="jobIssues.length"
                    class="mt-3 rounded-lg border border-zinc-200 bg-white p-3"
                  >
                    <p class="text-xs font-semibold uppercase tracking-wide text-zinc-500">
                      Row issues (first {{ Math.min(jobIssues.length, 10) }})
                    </p>
                    <ul class="mt-2 space-y-1.5 text-sm text-zinc-700">
                      <li
                        v-for="issue in jobIssues.slice(0, 10)"
                        :key="`job-issue-${issue.rowNumber}-${issue.action || issue.status}`"
                        class="leading-5"
                      >
                        <span class="font-semibold">Row {{ issue.rowNumber || '?' }}:</span>
                        {{ formatImportIssue(issue) }}
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </DialogPanel>
          </TransitionChild>
        </div>
      </div>
    </Dialog>
  </TransitionRoot>
</template>

<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, reactive, ref, watch } from 'vue';
import { Dialog, DialogPanel, DialogTitle, TransitionChild, TransitionRoot } from '@headlessui/vue';
import { DocumentArrowUpIcon, XMarkIcon } from '@heroicons/vue/24/outline';
import PlatformaSelect from '@/Components/PlatformaSelect.vue';

const props = defineProps({ open: { type: Boolean, default: false }, targetType: { type: String, default: 'member' } });
const emit = defineEmits(['update:open', 'import-complete']);
const apiProxyUrl = process.env.VUE_APP_API_PROXY_URL;
const IGNORE_VALUE = '__ignore__';
const SPREADSHEET_DEFAULT_ROWS = 500;
const SHEET_ROW_HEADER_WIDTH = 52;
const SHEET_MIN_COL_WIDTH = 120;
const SHEET_MIN_ROW_HEIGHT = 26;
const SHEET_DEFAULT_ROW_HEIGHT = 26;

const step = ref('source');
const sourceTab = ref('paste');
const fileInputRef = ref(null);
const sheetViewportRef = ref(null);
const sheetBottomScrollerRef = ref(null);
const sheetScrollSyncing = ref(false);
const isDragOver = ref(false);
const uploadedFileName = ref('');
const uploadedCsvText = ref('');
const spreadsheetRows = ref([]);
const planTierSlugOptions = ref([]);
const helperSelections = reactive({ planTierSlug: '', status: '', sendInviteEmail: '' });
const selectedCell = reactive({ row: 0, col: 0 });
const selectionAnchor = reactive({ active: false, row: 0, col: 0 });
const spreadsheetColumnWidths = ref([]);
const spreadsheetRowHeights = ref([]);
const activeSheetResize = reactive({ type: '', index: -1, startX: 0, startY: 0, startSize: 0 });
const permissionConfirmed = ref(false);
const importMode = ref('upsert');
const previewLoading = ref(false);
const startLoading = ref(false);
const errorMessage = ref('');
const headers = ref([]);
const previewRows = ref([]);
const previewBlockingIssues = ref([]);
const mappingOptions = ref([{ value: IGNORE_VALUE, label: 'Ignore column' }]);
const mapping = reactive({});
const previewSummary = reactive({ totalRows: 0, warningRows: 0, blockingRows: 0 });
const jobStatus = reactive({
  jobPublicId: '',
  status: '',
  totalRows: 0,
  processedRows: 0,
  successRows: 0,
  failedRows: 0,
  skippedRows: 0,
  warningRows: 0,
  errorMessage: '',
});
const jobIssues = ref([]);
let pollTimer = null;

const targetTypeLabel = computed(() => (String(props.targetType || '').toLowerCase() === 'lead' ? 'Leads' : 'Members'));
const runningHumanMessage = computed(() => {
  if (jobStatus.status === 'failed') {
    return jobStatus.errorMessage || 'Import failed before rows could be processed. Please review the row errors below and retry.';
  }
  if (jobStatus.status === 'completed_with_errors' || jobStatus.failedRows > 0) {
    return `Import finished with ${jobStatus.failedRows} failed row(s). Review the row errors below, fix those rows, and run import again.`;
  }
  if (jobStatus.status === 'completed' && jobStatus.successRows > 0) {
    return `Import completed successfully. ${jobStatus.successRows} row(s) imported.`;
  }
  return '';
});

const runningHumanMessageClass = computed(() => {
  const base = 'mt-3 rounded-lg border px-3 py-2 text-sm';
  const isSuccess = jobStatus.status === 'completed' && jobStatus.failedRows === 0 && jobStatus.successRows > 0;
  if (isSuccess) return `${base} border-emerald-200 bg-emerald-50 text-emerald-700`;
  return `${base} border-rose-200 bg-rose-50 text-rose-700`;
});

const formatImportIssue = (issue) => {
  const errorText = String(issue?.errorMessage || '').trim();
  if (errorText) return errorText;
  const warnings = Array.isArray(issue?.warnings) ? issue.warnings.filter(Boolean) : [];
  if (warnings.length) return warnings.join(' | ');
  const actionText = String(issue?.action || issue?.status || 'validation issue').trim();
  return actionText || 'Validation issue.';
};

const spreadsheetHeaders = computed(() => {
  const normalizedTarget = String(props.targetType || '').toLowerCase() === 'lead' ? 'lead' : 'member';
  return Array.isArray(templateHeadersByTarget[normalizedTarget]) ? [...templateHeadersByTarget[normalizedTarget]] : [...templateHeadersByTarget.member];
});
const toSpreadsheetColumnLetter = (index) => {
  let n = Number(index) + 1;
  let out = '';
  while (n > 0) {
    const rem = (n - 1) % 26;
    out = String.fromCharCode(65 + rem) + out;
    n = Math.floor((n - 1) / 26);
  }
  return out || 'A';
};
const spreadsheetColumnLetters = computed(() => spreadsheetHeaders.value.map((_, index) => toSpreadsheetColumnLetter(index)));
const sheetContentWidthPx = computed(() => {
  const widthSum = spreadsheetHeaders.value.reduce((sum, _header, index) => {
    const rawWidth = Number(spreadsheetColumnWidths.value[index] || 0);
    const resolvedWidth = Number.isFinite(rawWidth) && rawWidth > 0
      ? Math.max(SHEET_MIN_COL_WIDTH, rawWidth)
      : defaultSpreadsheetColumnWidth(spreadsheetHeaders.value[index] || '');
    return sum + resolvedWidth;
  }, SHEET_ROW_HEADER_WIDTH);
  return Math.max(2200, widthSum);
});
const spreadsheetExampleRowByTarget = Object.freeze({
  member: Object.freeze({
    first_name: 'Taylor',
    last_name: 'Mason',
    email: 'taylor@example.com',
    password: 'TempPass#2026',
    phone: '15551112222',
    alias: 'taylorm',
    subscription: 'Elite Membership',
    status: 'waiting for payment',
    stripe_customer_id: 'cus_123abc',
    next_charge_at: '2026-05-15T13:00:00Z',
    send_invite_email: 'true',
  }),
  lead: Object.freeze({
    first_name: 'Alex',
    last_name: 'Morgan',
    email: 'alex@example.com',
    password: 'TempPass#2026',
    phone: '15553334444',
    alias: 'alexm',
    subscription: 'Starter Tier',
    status: 'waiting for payment',
    stripe_customer_id: 'cus_987xyz',
    send_invite_email: 'false',
  }),
});
const spreadsheetColumnHints = computed(() => {
  const statusHint = 'Allowed: waiting for payment, active, inactive, paused, canceled, new, onboarding, pending_payment.';
  const tierHint = planTierSlugOptions.value.length
    ? `Use one of your active subscriptions: ${planTierSlugOptions.value.slice(0, 10).map((row) => row.value).join(', ')}${planTierSlugOptions.value.length > 10 ? ' ...' : ''}`
    : 'Use an active subscription name from Membership Settings.';
  return {
    first_name: 'Required. Member first name.',
    last_name: 'Optional. Member last name.',
    email: 'Required. Valid email format.',
    password: 'Required for new member creates.',
    phone: 'Optional phone text.',
    alias: 'Optional display alias.',
    subscription: tierHint,
    status: statusHint,
    stripe_customer_id: 'Optional existing Stripe customer id.',
    next_charge_at: 'Optional. ISO datetime preferred (e.g. 2026-05-15T13:00:00Z) or parseable date.',
    send_invite_email: 'Allowed values: true/false.',
  };
});
const hasSpreadsheetDataRows = computed(() => spreadsheetRows.value.some((row) =>
  spreadsheetHeaders.value.some((header) => String(row?.[header] || '').trim().length > 0)
));
const selectedCellLabel = computed(() => {
  const colLabel = spreadsheetColumnLetters.value[selectedCell.col] || 'A';
  return `${colLabel}${selectedCell.row + 2}`;
});
const selectedCellFieldLabel = computed(() => {
  const field = spreadsheetHeaders.value[selectedCell.col] || '';
  return field ? `Field: ${field}` : 'Select a cell';
});
const statusCellOptions = computed(() => ([
  { value: 'waiting for payment', label: 'waiting for payment' },
  { value: 'active', label: 'active' },
  { value: 'inactive', label: 'inactive' },
  { value: 'paused', label: 'paused' },
  { value: 'canceled', label: 'canceled' },
  { value: 'new', label: 'new' },
  { value: 'onboarding', label: 'onboarding' },
  { value: 'pending_payment', label: 'pending_payment' },
]));
const sendInviteCellOptions = Object.freeze([
  { value: 'true', label: 'true' },
  { value: 'false', label: 'false' },
]);
const csvTextValue = computed(() => (sourceTab.value === 'upload' ? uploadedCsvText.value : buildSpreadsheetCsvText()));
const canContinueToPreview = computed(() => {
  if (!permissionConfirmed.value) return false;
  if (sourceTab.value === 'upload') return Boolean(String(uploadedCsvText.value || '').trim());
  return hasSpreadsheetDataRows.value;
});
const importModeOptions = Object.freeze([{ value: 'upsert', label: 'Add and update existing' }, { value: 'insert_only', label: 'Add only new rows' }, { value: 'update_existing', label: 'Update existing only' }, { value: 'skip_existing', label: 'Skip existing rows' }]);
const templateHeadersByTarget = Object.freeze({
  member: Object.freeze([
    'first_name',
    'last_name',
    'email',
    'password',
    'phone',
    'alias',
    'subscription',
    'status',
    'stripe_customer_id',
    'next_charge_at',
    'send_invite_email',
  ]),
  lead: Object.freeze([
    'first_name',
    'last_name',
    'email',
    'password',
    'phone',
    'alias',
    'subscription',
    'status',
    'stripe_customer_id',
    'send_invite_email',
  ]),
});

const defaultSpreadsheetColumnWidth = (header) => {
  const key = String(header || '').trim().toLowerCase();
  if (key === 'email') return 240;
  if (key === 'next_charge_at') return 220;
  if (key === 'stripe_customer_id') return 190;
  if (key === 'subscription') return 220;
  if (key === 'password') return 170;
  if (key === 'phone') return 160;
  if (key === 'alias') return 170;
  if (key === 'status') return 150;
  if (key === 'send_invite_email') return 170;
  return 180;
};

const syncSpreadsheetDimensions = () => {
  const headersForTarget = [...spreadsheetHeaders.value];
  const nextWidths = headersForTarget.map((header, index) => {
    const current = Number(spreadsheetColumnWidths.value[index] || 0);
    if (Number.isFinite(current) && current >= SHEET_MIN_COL_WIDTH) return current;
    return defaultSpreadsheetColumnWidth(header);
  });
  spreadsheetColumnWidths.value = nextWidths;
  const rowCount = spreadsheetRows.value.length;
  if (!Number.isFinite(rowCount) || rowCount <= 0) {
    spreadsheetRowHeights.value = [];
    return;
  }
  const nextHeights = Array.from({ length: rowCount }, (_unused, index) => {
    const current = Number(spreadsheetRowHeights.value[index] || 0);
    if (Number.isFinite(current) && current >= SHEET_MIN_ROW_HEIGHT) return current;
    return SHEET_DEFAULT_ROW_HEIGHT;
  });
  spreadsheetRowHeights.value = nextHeights;
};

const getRowHeight = (rowIndex) => {
  const normalized = Math.max(0, Number(rowIndex) || 0);
  const value = Number(spreadsheetRowHeights.value[normalized] || 0);
  if (Number.isFinite(value) && value >= SHEET_MIN_ROW_HEIGHT) return value;
  return SHEET_DEFAULT_ROW_HEIGHT;
};

const getRowStyle = (rowIndex) => ({
  height: `${getRowHeight(rowIndex)}px`,
});

const SHEET_INPUT_LINE_HEIGHT_PX = 16;
const getCellInputStyle = (rowIndex) => {
  const height = getRowHeight(rowIndex);
  const extra = Math.max(0, height - SHEET_INPUT_LINE_HEIGHT_PX);
  const padTop = Math.floor(extra / 2);
  const padBottom = extra - padTop;
  return {
    minHeight: `${height}px`,
    height: `${height}px`,
    lineHeight: `${SHEET_INPUT_LINE_HEIGHT_PX}px`,
    paddingTop: `${padTop}px`,
    paddingBottom: `${padBottom}px`,
  };
};

const getColumnStyle = (colIndex) => {
  const normalized = Math.max(0, Number(colIndex) || 0);
  const current = Number(spreadsheetColumnWidths.value[normalized] || 0);
  const fallback = defaultSpreadsheetColumnWidth(spreadsheetHeaders.value[normalized] || '');
  const width = Math.max(SHEET_MIN_COL_WIDTH, Number.isFinite(current) && current > 0 ? current : fallback);
  return { width: `${width}px`, minWidth: `${width}px` };
};

const createSpreadsheetRow = () => spreadsheetHeaders.value.reduce((acc, header) => {
  acc[header] = '';
  return acc;
}, {});

const seedSpreadsheetExampleRow = () => {
  ensureSpreadsheetRowCount(1);
  const normalizedTarget = String(props.targetType || '').toLowerCase() === 'lead' ? 'lead' : 'member';
  const example = spreadsheetExampleRowByTarget[normalizedTarget] || spreadsheetExampleRowByTarget.member;
  const firstRow = spreadsheetRows.value[0] || createSpreadsheetRow();
  spreadsheetHeaders.value.forEach((header) => {
    firstRow[header] = String(example?.[header] || '');
  });
  spreadsheetRows.value[0] = firstRow;
};

const ensureSpreadsheetRowCount = (minRows = 1) => {
  const requiredRows = Math.max(1, Number(minRows) || 1);
  while (spreadsheetRows.value.length < requiredRows) {
    spreadsheetRows.value.push(createSpreadsheetRow());
  }
  syncSpreadsheetDimensions();
};

const appendSpreadsheetRows = (count = 1) => {
  const nextCount = Math.max(1, Number(count) || 1);
  for (let i = 0; i < nextCount; i += 1) {
    spreadsheetRows.value.push(createSpreadsheetRow());
  }
  syncSpreadsheetDimensions();
};

const clearSpreadsheetRows = () => {
  spreadsheetRows.value = [];
  appendSpreadsheetRows(SPREADSHEET_DEFAULT_ROWS);
  seedSpreadsheetExampleRow();
};

const setSelectedCell = (rowIndex, colIndex) => {
  selectedCell.row = Math.max(0, Number(rowIndex) || 0);
  selectedCell.col = Math.max(0, Number(colIndex) || 0);
};

const selectionBounds = computed(() => {
  if (!selectionAnchor.active) return null;
  const minRow = Math.min(selectionAnchor.row, selectedCell.row);
  const maxRow = Math.max(selectionAnchor.row, selectedCell.row);
  const minCol = Math.min(selectionAnchor.col, selectedCell.col);
  const maxCol = Math.max(selectionAnchor.col, selectedCell.col);
  return { minRow, maxRow, minCol, maxCol };
});

const isCellSelected = (rowIndex, colIndex) => {
  const bounds = selectionBounds.value;
  if (!bounds) return rowIndex === selectedCell.row && colIndex === selectedCell.col;
  return rowIndex >= bounds.minRow
    && rowIndex <= bounds.maxRow
    && colIndex >= bounds.minCol
    && colIndex <= bounds.maxCol;
};

const onCellClick = (rowIndex, colIndex, event) => {
  const row = Math.max(0, Number(rowIndex) || 0);
  const col = Math.max(0, Number(colIndex) || 0);
  if (event?.shiftKey && selectionAnchor.active) {
    setSelectedCell(row, col);
  } else {
    selectionAnchor.active = true;
    selectionAnchor.row = row;
    selectionAnchor.col = col;
    setSelectedCell(row, col);
  }
  focusCell(row, col);
};

const focusCell = (rowIndex, colIndex) => {
  setSelectedCell(rowIndex, colIndex);
  nextTick(() => {
    const selector = `textarea[data-sheet-row="${selectedCell.row}"][data-sheet-col="${selectedCell.col}"]`;
    document.querySelector(selector)?.focus?.();
  });
};

const beginSheetResize = ({ type, index, startX, startY, startSize }) => {
  activeSheetResize.type = String(type || '');
  activeSheetResize.index = Math.max(0, Number(index) || 0);
  activeSheetResize.startX = Number(startX) || 0;
  activeSheetResize.startY = Number(startY) || 0;
  activeSheetResize.startSize = Math.max(0, Number(startSize) || 0);
};

const stopSheetResize = () => {
  activeSheetResize.type = '';
  activeSheetResize.index = -1;
  activeSheetResize.startX = 0;
  activeSheetResize.startY = 0;
  activeSheetResize.startSize = 0;
};

const startColumnResize = (colIndex, event) => {
  const index = Math.max(0, Number(colIndex) || 0);
  const currentWidth = Number(spreadsheetColumnWidths.value[index] || 0) || defaultSpreadsheetColumnWidth(spreadsheetHeaders.value[index] || '');
  beginSheetResize({
    type: 'column',
    index,
    startX: event?.clientX || 0,
    startY: event?.clientY || 0,
    startSize: currentWidth,
  });
};

const startRowResize = (rowIndex, event) => {
  const index = Math.max(0, Number(rowIndex) || 0);
  const currentHeight = getRowHeight(index);
  beginSheetResize({
    type: 'row',
    index,
    startX: event?.clientX || 0,
    startY: event?.clientY || 0,
    startSize: currentHeight,
  });
};

const handleGlobalSheetResizeMove = (event) => {
  const resizeType = String(activeSheetResize.type || '');
  if (!resizeType) return;
  if (resizeType === 'column') {
    const deltaX = Number(event?.clientX || 0) - activeSheetResize.startX;
    const nextWidth = Math.max(SHEET_MIN_COL_WIDTH, Math.round(activeSheetResize.startSize + deltaX));
    if (!Array.isArray(spreadsheetColumnWidths.value)) return;
    if (activeSheetResize.index < 0 || activeSheetResize.index >= spreadsheetColumnWidths.value.length) return;
    spreadsheetColumnWidths.value[activeSheetResize.index] = nextWidth;
    return;
  }
  if (resizeType === 'row') {
    const deltaY = Number(event?.clientY || 0) - activeSheetResize.startY;
    const nextHeight = Math.max(SHEET_MIN_ROW_HEIGHT, Math.round(activeSheetResize.startSize + deltaY));
    if (!Array.isArray(spreadsheetRowHeights.value)) return;
    if (activeSheetResize.index < 0 || activeSheetResize.index >= spreadsheetRowHeights.value.length) return;
    spreadsheetRowHeights.value[activeSheetResize.index] = nextHeight;
  }
};

const handleGlobalSheetResizeStop = () => {
  if (!String(activeSheetResize.type || '')) return;
  stopSheetResize();
};

const handleSheetViewportScroll = () => {
  const viewport = sheetViewportRef.value;
  const bottom = sheetBottomScrollerRef.value;
  if (!viewport || !bottom) return;
  if (sheetScrollSyncing.value) return;
  sheetScrollSyncing.value = true;
  bottom.scrollLeft = viewport.scrollLeft;
  requestAnimationFrame(() => {
    sheetScrollSyncing.value = false;
  });
};

const handleSheetBottomScroll = () => {
  const viewport = sheetViewportRef.value;
  const bottom = sheetBottomScrollerRef.value;
  if (!viewport || !bottom) return;
  if (sheetScrollSyncing.value) return;
  sheetScrollSyncing.value = true;
  viewport.scrollLeft = bottom.scrollLeft;
  requestAnimationFrame(() => {
    sheetScrollSyncing.value = false;
  });
};

const insertIntoSelectedCell = (value) => {
  const nextValue = String(value || '').trim();
  if (!nextValue) return;
  ensureSpreadsheetRowCount(selectedCell.row + 1);
  const header = spreadsheetHeaders.value[selectedCell.col];
  if (!header) return;
  spreadsheetRows.value[selectedCell.row][header] = nextValue;
};

const escapeCsvValue = (rawValue) => {
  const token = String(rawValue ?? '');
  if (!/[",\n\r]/.test(token)) return token;
  return `"${token.replace(/"/g, '""')}"`;
};

const buildSpreadsheetCsvText = () => {
  const headersForTarget = spreadsheetHeaders.value;
  const nonEmptyRows = spreadsheetRows.value.filter((row) =>
    headersForTarget.some((header) => String(row?.[header] || '').trim().length > 0)
  );
  const csvLines = [headersForTarget.map((header) => escapeCsvValue(header)).join(',')];
  nonEmptyRows.forEach((row) => {
    const line = headersForTarget.map((header) => escapeCsvValue(row?.[header] || '')).join(',');
    csvLines.push(line);
  });
  return `${csvLines.join('\n')}\n`;
};

const parseDelimitedRow = (line, delimiter) => {
  const values = [];
  let current = '';
  let inQuotes = false;
  for (let i = 0; i < line.length; i += 1) {
    const ch = line[i];
    if (inQuotes) {
      if (ch === '"') {
        if (line[i + 1] === '"') {
          current += '"';
          i += 1;
        } else {
          inQuotes = false;
        }
      } else {
        current += ch;
      }
      continue;
    }
    if (ch === '"') {
      inQuotes = true;
      continue;
    }
    if (ch === delimiter) {
      values.push(current);
      current = '';
      continue;
    }
    current += ch;
  }
  values.push(current);
  return values;
};

const parseClipboardMatrix = (rawText) => {
  const raw = String(rawText || '').replace(/\r/g, '').trim();
  if (!raw) return [];
  const lines = raw
    .split(/\r?\n/g)
    .map((line) => String(line || ''))
    .filter((line) => line.trim().length > 0);
  if (!lines.length) return [];
  const delimiter = lines.some((line) => line.includes('\t')) ? '\t' : ',';
  return lines.map((line) => parseDelimitedRow(line, delimiter));
};

const applyClipboardMatrixToGrid = (matrix, startRow, startCol) => {
  const rows = Array.isArray(matrix) ? matrix : [];
  if (!rows.length) return;
  const maxColumns = spreadsheetHeaders.value.length;
  const targetStartRow = Math.max(0, Number(startRow) || 0);
  const targetStartCol = Math.max(0, Number(startCol) || 0);
  ensureSpreadsheetRowCount(targetStartRow + rows.length);
  rows.forEach((cells, rOffset) => {
    const rowIndex = targetStartRow + rOffset;
    if (!spreadsheetRows.value[rowIndex]) spreadsheetRows.value[rowIndex] = createSpreadsheetRow();
    cells.forEach((cellValue, cOffset) => {
      const colIndex = targetStartCol + cOffset;
      if (colIndex < 0 || colIndex >= maxColumns) return;
      const header = spreadsheetHeaders.value[colIndex];
      spreadsheetRows.value[rowIndex][header] = String(cellValue ?? '').trim();
    });
  });
};

const applySingleValueToSelection = (value) => {
  const bounds = selectionBounds.value;
  if (!bounds) return false;
  if (bounds.minRow === bounds.maxRow && bounds.minCol === bounds.maxCol) return false;
  ensureSpreadsheetRowCount(bounds.maxRow + 1);
  for (let row = bounds.minRow; row <= bounds.maxRow; row += 1) {
    for (let col = bounds.minCol; col <= bounds.maxCol; col += 1) {
      const header = spreadsheetHeaders.value[col];
      if (!header) continue;
      spreadsheetRows.value[row][header] = value;
    }
  }
  return true;
};

const handleCellPaste = (event, rowIndex, colIndex) => {
  const raw = event?.clipboardData?.getData('text/plain') || '';
  if (!String(raw || '').trim()) return;
  event.preventDefault();
  const matrix = parseClipboardMatrix(raw);
  if (matrix.length === 1 && (matrix[0] || []).length === 1) {
    const value = String(matrix[0][0] ?? '').trim();
    const appliedToSelection = applySingleValueToSelection(value);
    if (appliedToSelection) return;
  }
  const bounds = selectionBounds.value;
  const startRow = bounds ? bounds.minRow : rowIndex;
  const startCol = bounds ? bounds.minCol : colIndex;
  applyClipboardMatrixToGrid(matrix, startRow, startCol);
  setSelectedCell(startRow, startCol);
};

const creatorPaymentsPathCandidates = (path) => {
  const suffix = String(path || '').startsWith('/') ? String(path || '') : `/${String(path || '')}`;
  return [`/api/creator-payments${suffix}`, `/api/payments${suffix}`];
};

const requestCreatorPayments = async ({ path } = {}) => {
  let lastMessage = '';
  for (const candidate of creatorPaymentsPathCandidates(path)) {
    const response = await fetch(`${apiProxyUrl}${candidate}`, {
      method: 'GET',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
    });
    const payload = await response.json().catch(() => ({}));
    if (response.ok && payload?.success !== false) return payload;
    lastMessage = String(payload?.message || payload?.error || `Request failed (${response.status})`);
    if (response.status !== 404) break;
  }
  throw new Error(lastMessage || 'Request failed');
};

const loadPlanTierSlugOptions = async () => {
  try {
    const payload = await requestCreatorPayments({ path: '/creator/plan-tiers?page=1&pageSize=300&scope=all' });
    const rows = Array.isArray(payload?.tiers) ? payload.tiers : [];
    const normalizedRows = rows
      .filter((tier) => String(tier?.status || '').trim().toLowerCase() === 'active')
      .map((tier) => {
        const slug = String(tier?.slug || '').trim();
        const name = String(tier?.name || '').trim();
        if (!slug && !name) return null;
        return {
          value: name || slug,
          label: name || slug,
        };
      })
      .filter(Boolean);
    planTierSlugOptions.value = normalizedRows;
  } catch (_error) {
    planTierSlugOptions.value = [];
  }
};

const downloadTemplateCsv = () => {
  const normalizedTarget = String(props.targetType || '').toLowerCase() === 'lead' ? 'lead' : 'member';
  const headersForTarget = Array.isArray(templateHeadersByTarget[normalizedTarget]) ? templateHeadersByTarget[normalizedTarget] : templateHeadersByTarget.member;
  const exampleRow = spreadsheetExampleRowByTarget[normalizedTarget] || spreadsheetExampleRowByTarget.member;
  const sampleLine = headersForTarget.map((header) => escapeCsvValue(exampleRow[header] || '')).join(',');
  const csvBody = `${headersForTarget.join(',')}\n${sampleLine}\n`;
  const blob = new Blob([`\uFEFF${csvBody}`], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  const suffix = normalizedTarget === 'lead' ? 'LEAD' : 'MEMBER';
  const now = new Date();
  const stamp = [
    now.getFullYear(),
    String(now.getMonth() + 1).padStart(2, '0'),
    String(now.getDate()).padStart(2, '0'),
    '_',
    String(now.getHours()).padStart(2, '0'),
    String(now.getMinutes()).padStart(2, '0'),
    String(now.getSeconds()).padStart(2, '0'),
  ].join('');
  link.href = url;
  link.download = `MY_PLATFORMA_BULK_${suffix}_UPLOAD_FILE_TEMPLATE_${stamp}.csv`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

const handleClose = () => { if (previewLoading.value || startLoading.value) return; clearPollTimer(); emit('update:open', false); };
const handleBackOrClose = () => { if (step.value === 'source') handleClose(); else if (step.value === 'mapping') step.value = 'source'; else handleClose(); };
const openFilePicker = () => { fileInputRef.value?.click?.(); };
const handleCsvFileChosen = async (event) => { const file = event?.target?.files?.[0]; if (!file) return; uploadedFileName.value = String(file.name || ''); uploadedCsvText.value = await file.text(); };
const handleDropCsv = async (event) => { isDragOver.value = false; const file = event?.dataTransfer?.files?.[0]; if (!file) return; uploadedFileName.value = String(file.name || ''); uploadedCsvText.value = await file.text(); };
const clearPollTimer = () => { if (!pollTimer) return; clearInterval(pollTimer); pollTimer = null; };

const runPreview = async () => {
  errorMessage.value = '';
  previewLoading.value = true;
  try {
    const response = await fetch(`${apiProxyUrl}/api/member-imports/preview`, { method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ targetType: props.targetType, importMode: importMode.value, csvText: csvTextValue.value, mapping: { ...mapping } }) });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok || payload?.success === false) throw new Error(String(payload?.message || `Preview failed (${response.status})`));
    headers.value = Array.isArray(payload?.headers) ? payload.headers : [];
    previewRows.value = Array.isArray(payload?.previewRows) ? payload.previewRows : [];
    previewBlockingIssues.value = Array.isArray(payload?.mappingBlockingIssues) ? payload.mappingBlockingIssues : [];
    previewSummary.totalRows = Number(payload?.totalRows || 0);
    previewSummary.warningRows = Number(payload?.warningRows || 0);
    previewSummary.blockingRows = Number(payload?.blockingRows || 0);
    const canonical = Array.isArray(payload?.canonicalFields) ? payload.canonicalFields : [];
    mappingOptions.value = [{ value: IGNORE_VALUE, label: 'Ignore column' }, ...canonical.map((row) => ({ value: String(row?.key || ''), label: `${String(row?.label || row?.key || '')}${row?.required ? ' (Required)' : ''}` })).filter((row) => row.value)];
    const nextMapping = payload?.mapping && typeof payload.mapping === 'object' ? payload.mapping : {};
    headers.value.forEach((header) => { mapping[header] = String(nextMapping[header] || IGNORE_VALUE); });
    step.value = 'mapping';
  } catch (error) {
    errorMessage.value = String(error?.message || 'Failed to preview import.');
  } finally {
    previewLoading.value = false;
  }
};

const refreshJobStatus = async () => {
  if (!jobStatus.jobPublicId) return;
  const response = await fetch(`${apiProxyUrl}/api/member-imports/${encodeURIComponent(jobStatus.jobPublicId)}`, { method: 'GET', credentials: 'include' });
  const payload = await response.json().catch(() => ({}));
  if (!response.ok || payload?.success === false) throw new Error(String(payload?.message || `Failed polling job (${response.status})`));
  const job = payload?.job || {};
  jobStatus.status = String(job?.status || '');
  jobStatus.totalRows = Number(job?.totalRows || 0);
  jobStatus.processedRows = Number(job?.processedRows || 0);
  jobStatus.successRows = Number(job?.successRows || 0);
  jobStatus.failedRows = Number(job?.failedRows || 0);
  jobStatus.skippedRows = Number(job?.skippedRows || 0);
  jobStatus.warningRows = Number(job?.warningRows || 0);
  jobStatus.errorMessage = String(job?.errorMessage || '');
  jobIssues.value = Array.isArray(payload?.issues) ? payload.issues : [];
  if ((jobStatus.failedRows > 0 || jobStatus.status === 'failed') && jobIssues.value.length) {
    const firstIssue = jobIssues.value[0] || {};
    console.warn('[BulkImport] first row issue:', {
      rowNumber: firstIssue?.rowNumber,
      action: firstIssue?.action,
      status: firstIssue?.status,
      errorMessage: firstIssue?.errorMessage,
      warnings: firstIssue?.warnings,
    });
  }
  if (['completed', 'completed_with_errors', 'failed', 'canceled'].includes(jobStatus.status)) {
    clearPollTimer();
    emit('import-complete', { ...job });
  }
};

const startImport = async () => {
  errorMessage.value = '';
  startLoading.value = true;
  try {
    const mappingPayload = {};
    headers.value.forEach((header) => { const val = String(mapping[header] || IGNORE_VALUE); mappingPayload[header] = val === IGNORE_VALUE ? '' : val; });
    const response = await fetch(`${apiProxyUrl}/api/member-imports/start`, { method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ targetType: props.targetType, importMode: importMode.value, csvText: csvTextValue.value, permissionConfirmed: permissionConfirmed.value ? 1 : 0, mapping: mappingPayload }) });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok || payload?.success === false) throw new Error(String(payload?.message || `Failed starting import (${response.status})`));
    const job = payload?.job || {};
    jobStatus.jobPublicId = String(job?.jobPublicId || '');
    jobStatus.status = String(job?.status || 'queued');
    jobStatus.totalRows = Number(job?.totalRows || 0);
    jobStatus.processedRows = 0;
    jobStatus.successRows = 0;
    jobStatus.failedRows = 0;
    jobStatus.skippedRows = 0;
    jobStatus.warningRows = 0;
    jobStatus.errorMessage = '';
    jobIssues.value = [];
    step.value = 'running';
    clearPollTimer();
    await refreshJobStatus();
    pollTimer = setInterval(() => {
      void refreshJobStatus().catch((pollError) => {
        errorMessage.value = String(pollError?.message || 'Failed refreshing import progress.');
        clearPollTimer();
      });
    }, 1800);
  } catch (error) {
    errorMessage.value = String(error?.message || 'Failed to queue import.');
  } finally {
    startLoading.value = false;
  }
};

watch(
  () => spreadsheetHeaders.value.join('|'),
  () => {
    syncSpreadsheetDimensions();
  },
  { immediate: true }
);

watch(
  () => spreadsheetRows.value.length,
  () => {
    syncSpreadsheetDimensions();
  }
);

watch(
  () => sheetContentWidthPx.value,
  () => {
    requestAnimationFrame(() => {
      handleSheetViewportScroll();
    });
  }
);

watch(() => props.open, (isOpen) => {
  if (!isOpen) return;
  step.value = 'source';
  sourceTab.value = 'paste';
  uploadedFileName.value = '';
  uploadedCsvText.value = '';
  spreadsheetRows.value = [];
  appendSpreadsheetRows(SPREADSHEET_DEFAULT_ROWS);
  seedSpreadsheetExampleRow();
  selectedCell.row = 0;
  selectedCell.col = 0;
  selectionAnchor.active = true;
  selectionAnchor.row = 0;
  selectionAnchor.col = 0;
  helperSelections.planTierSlug = '';
  helperSelections.status = '';
  helperSelections.sendInviteEmail = '';
  sheetScrollSyncing.value = false;
  void loadPlanTierSlugOptions();
  permissionConfirmed.value = false;
  importMode.value = 'upsert';
  errorMessage.value = '';
  headers.value = [];
  previewRows.value = [];
  previewBlockingIssues.value = [];
  previewSummary.totalRows = 0; previewSummary.warningRows = 0; previewSummary.blockingRows = 0;
  jobStatus.jobPublicId = ''; jobStatus.status = ''; jobStatus.totalRows = 0; jobStatus.processedRows = 0; jobStatus.successRows = 0; jobStatus.failedRows = 0; jobStatus.skippedRows = 0; jobStatus.warningRows = 0; jobStatus.errorMessage = '';
  jobIssues.value = [];
  clearPollTimer();
});

watch(
  () => props.targetType,
  () => {
    spreadsheetRows.value = [];
    appendSpreadsheetRows(SPREADSHEET_DEFAULT_ROWS);
    seedSpreadsheetExampleRow();
    selectedCell.row = 0;
    selectedCell.col = 0;
    selectionAnchor.active = true;
    selectionAnchor.row = 0;
    selectionAnchor.col = 0;
    helperSelections.planTierSlug = '';
    helperSelections.status = '';
    helperSelections.sendInviteEmail = '';
    sheetScrollSyncing.value = false;
    errorMessage.value = '';
    jobStatus.jobPublicId = '';
    jobStatus.status = '';
    jobStatus.totalRows = 0;
    jobStatus.processedRows = 0;
    jobStatus.successRows = 0;
    jobStatus.failedRows = 0;
    jobStatus.skippedRows = 0;
    jobStatus.warningRows = 0;
    jobStatus.errorMessage = '';
    jobIssues.value = [];
    clearPollTimer();
    if (props.open) {
      void loadPlanTierSlugOptions();
    }
  }
);

onBeforeUnmount(() => clearPollTimer());
onMounted(() => {
  if (typeof window === 'undefined' || !window.addEventListener) return;
  window.addEventListener('mousemove', handleGlobalSheetResizeMove);
  window.addEventListener('mouseup', handleGlobalSheetResizeStop);
  window.addEventListener('mouseleave', handleGlobalSheetResizeStop);
});

onBeforeUnmount(() => {
  if (typeof window === 'undefined' || !window.removeEventListener) return;
  window.removeEventListener('mousemove', handleGlobalSheetResizeMove);
  window.removeEventListener('mouseup', handleGlobalSheetResizeStop);
  window.removeEventListener('mouseleave', handleGlobalSheetResizeStop);
  stopSheetResize();
});
</script>

<style scoped>
.app-scroll {
  scrollbar-width: thin;
  scrollbar-color: rgba(113, 113, 122, 0.42) transparent;
}

.app-scroll::-webkit-scrollbar { width: 7px; }
.app-scroll::-webkit-scrollbar-track { background: transparent; }
.app-scroll::-webkit-scrollbar-thumb { border-radius: 999px; background: rgba(113, 113, 122, 0.35); }
.app-scroll::-webkit-scrollbar-thumb:hover { background: rgba(82, 82, 91, 0.5); }

.sheet-x-scroll {
  scrollbar-width: thin;
  scrollbar-color: rgba(82, 82, 91, 0.58) rgba(228, 228, 231, 0.95);
  scrollbar-gutter: stable;
}

.sheet-x-scroll::-webkit-scrollbar {
  width: 11px;
  height: 11px;
}

.sheet-x-scroll::-webkit-scrollbar-track {
  background: rgba(228, 228, 231, 0.95);
  border-radius: 999px;
}

.sheet-x-scroll::-webkit-scrollbar-thumb {
  border-radius: 999px;
  background: rgba(82, 82, 91, 0.58);
}

.sheet-x-scroll::-webkit-scrollbar-thumb:hover {
  background: rgba(63, 63, 70, 0.72);
}

.sheet-x-scroll::-webkit-scrollbar-corner {
  background: rgba(228, 228, 231, 0.95);
}

.sheet-grid-scroll {
  --sheet-letter-row-h: 28px;
  scrollbar-gutter: stable;
}

.sheet-grid-scroll::-webkit-scrollbar:horizontal {
  height: 0;
}

.sheet-bottom-scroll {
  border-top: 1px solid rgb(228 228 231 / 1);
  background: rgb(244 244 245 / 0.72);
  min-height: 16px;
  position: relative;
  z-index: 45;
}

.sheet-th-letter {
  position: sticky;
  top: 0;
  z-index: 36;
  background: rgb(244 244 245 / 1);
  height: var(--sheet-letter-row-h);
  min-height: var(--sheet-letter-row-h);
  line-height: var(--sheet-letter-row-h);
}

.sheet-th-field {
  position: sticky;
  top: calc(var(--sheet-letter-row-h) - 1px);
  z-index: 35;
  background: rgb(250 250 250 / 1);
}

.sheet-th-corner-top {
  z-index: 38;
}

.sheet-th-corner-bottom {
  z-index: 37;
}

.sheet-th-left-col {
  position: sticky;
  left: 0;
  z-index: 42;
  isolation: isolate;
}

.sheet-left-col {
  position: sticky;
  left: 0;
  z-index: 24;
  background: rgb(250 250 250 / 1);
  isolation: isolate;
}

.sheet-th-left-col::before,
.sheet-left-col::before {
  content: '';
  position: absolute;
  left: -18px;
  top: -1px;
  bottom: -1px;
  width: 18px;
  background: inherit;
  pointer-events: none;
  z-index: -1;
}

.sheet-col-letter-header,
.sheet-row-header {
  user-select: none;
}

.sheet-col-resize-handle {
  position: absolute;
  top: -2px;
  right: -4px;
  bottom: -2px;
  width: 9px;
  cursor: col-resize;
  z-index: 20;
}

.sheet-col-resize-handle::after {
  content: none;
}

.sheet-col-resize-handle:hover::after {
  content: none;
}

.sheet-row-resize-handle {
  position: absolute;
  left: 0;
  right: 0;
  bottom: -4px;
  height: 9px;
  cursor: row-resize;
  z-index: 20;
}

.sheet-row-resize-handle::after {
  content: none;
}

.sheet-row-resize-handle:hover::after {
  content: none;
}

.sheet-cell {
  position: relative;
}

.sheet-cell::after {
  content: '';
  position: absolute;
  inset: -1px;
  border: 2px solid transparent;
  pointer-events: none;
  z-index: 3;
}

.sheet-cell-selected::after {
  border-color: #2d8cff;
}

.sheet-cell-input {
  display: block;
  box-sizing: border-box;
  margin: 0;
  cursor: default;
  resize: none;
}
</style>

